import {
  cartId,
  money,
  percentage,
  productId,
  promotionId,
  quantity,
  type AppliedPromotion,
  type Cart,
  type CartItem,
  type Promotion,
  type PromotionKind,
} from '@sshop/domain';

import type { AppliedPromotionDto, CartDto, CartItemDto, PromotionDto } from './cart.dto';

function toPromotionKind(dto: PromotionDto['kind']): PromotionKind {
  switch (dto.type) {
    case 'percent-off':
      return { type: 'percent-off', percent: percentage(dto.percent) };
    case 'amount-off':
      return { type: 'amount-off', amount: money(dto.amount.amount, dto.amount.currency) };
    case 'free-shipping':
      return { type: 'free-shipping' };
    case 'gift':
      return { type: 'gift', description: dto.description };
  }
}

function toPromotion(dto: PromotionDto): Promotion {
  return {
    id: promotionId(dto.id),
    code: dto.code,
    label: dto.label,
    kind: toPromotionKind(dto.kind),
    startsAt: dto.starts_at,
    endsAt: dto.ends_at,
  };
}

function toAppliedPromotion(dto: AppliedPromotionDto): AppliedPromotion {
  return {
    promotion: toPromotion(dto.promotion),
    discountAmount: money(dto.discount_amount.amount, dto.discount_amount.currency),
  };
}

function toCartItem(dto: CartItemDto): CartItem {
  return {
    productId: productId(dto.product_id),
    sku: dto.sku,
    slug: dto.slug,
    title: dto.title,
    image: dto.image
      ? {
          url: dto.image.url,
          alt: dto.image.alt,
          ...(dto.image.width !== undefined ? { width: dto.image.width } : {}),
          ...(dto.image.height !== undefined ? { height: dto.image.height } : {}),
        }
      : null,
    characteristics: dto.characteristics.map((c) => ({ name: c.name, value: c.value })),
    unitPrice: money(dto.unit_price.amount, dto.unit_price.currency),
    compareAtPrice: dto.compare_at_price
      ? money(dto.compare_at_price.amount, dto.compare_at_price.currency)
      : null,
    quantity: quantity(dto.quantity),
    lineTotal: money(dto.line_total.amount, dto.line_total.currency),
    availableQuantity: dto.available_quantity,
  };
}

export function toCart(dto: CartDto): Cart {
  return {
    id: cartId(dto.id),
    items: dto.items.map(toCartItem),
    itemCount: dto.item_count,
    currency: dto.currency,
    totals: {
      subtotal: money(dto.totals.subtotal.amount, dto.totals.subtotal.currency),
      discount: money(dto.totals.discount.amount, dto.totals.discount.currency),
      shipping: money(dto.totals.shipping.amount, dto.totals.shipping.currency),
      tax: money(dto.totals.tax.amount, dto.totals.tax.currency),
      total: money(dto.totals.total.amount, dto.totals.total.currency),
    },
    promotions: dto.promotions.map(toAppliedPromotion),
    updatedAt: dto.updated_at,
  };
}
